import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

const GA_MEASUREMENT_ID = 'G-F653X91YS8';

// Track page views
export const trackPageView = (path: string, title?: string): void => {
  if (!window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
};

// Track custom events
export const trackEvent = (
  eventName: string,
  params?: Record<string, unknown>
): void => {
  if (!window.gtag) return;

  window.gtag('event', eventName, params);
};

// Track phone call clicks (conversion)
export const trackPhoneClick = (phoneNumber: string): void => {
  trackEvent('phone_call_click', {
    event_category: 'Contact',
    event_label: phoneNumber,
    value: 1,
  });
  
  // Also track as conversion
  trackEvent('conversion', {
    send_to: `${GA_MEASUREMENT_ID}/phone_call`,
  });
};

// Track email clicks (conversion)
export const trackEmailClick = (email: string): void => {
  trackEvent('email_click', {
    event_category: 'Contact',
    event_label: email,
    value: 1,
  });
};

// Track emergency service clicks (high-value conversion)
export const trackEmergencyClick = (): void => {
  trackEvent('emergency_call_click', {
    event_category: 'Emergency',
    event_label: 'Emergency Service Request',
    value: 5,
  });
  
  // Track as high-priority conversion
  trackEvent('conversion', {
    send_to: `${GA_MEASUREMENT_ID}/emergency_call`,
  });
};

// Track service inquiries
export const trackServiceInquiry = (serviceName: string): void => {
  trackEvent('service_inquiry', {
    event_category: 'Services',
    event_label: serviceName,
  });
};

// Track portfolio views
export const trackPortfolioView = (projectName: string): void => {
  trackEvent('portfolio_view', {
    event_category: 'Portfolio',
    event_label: projectName,
  });
};

// Track outbound link clicks
export const trackOutboundLink = (url: string): void => {
  trackEvent('outbound_click', {
    event_category: 'Outbound',
    event_label: url,
  });
};

// Component that handles automatic page view tracking for SPA
export const GoogleAnalytics: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

export default GoogleAnalytics;
